import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import Footer from '../components/Footer';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white flex flex-col">
        <link rel="canonical" href="https://www.lingu-pix.app/privacy-policy" />
        <title>Privacy Policy – LinguPix</title>
        <meta name="description" content="Privacy Policy for the LinguPix language learning application and website." />

      {/* Header */}
      <header className="bg-[#333333] text-white py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center text-[#FFA55A] hover:text-white transition-colors mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Home
          </Link>
          <h1 className="text-4xl font-bold">Privacy Policy</h1>
          <p className="text-gray-300 mt-2">Last updated: March 26, 2026</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex-1">
        <div className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">1. Who We Are</h2>
            <p className="text-[#666666] leading-relaxed mb-6">LinguPix ("LinguPix", "we", "us", "our") provides a mobile language-learning application for children that is set up and managed by a parent, guardian, educator, or other adult account holder, as well as the companion website available at <code className="bg-gray-100 px-1 py-0.5 rounded text-sm text-[#FF7F30]">https://www.lingu-pix.app</code>.</p>
            <p className="text-[#666666] leading-relaxed mb-6">For privacy questions, data rights requests, and account deletion matters, contact:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li><a href="mailto:contact@lingu-pix.app" className="text-[#FF7F30] hover:text-[#FFA55A] font-semibold underline">contact@lingu-pix.app</a></li>
            </ul>
            <p className="text-[#666666] leading-relaxed mb-6">For general product support, you may also contact:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li><a href="mailto:support@lingu-pix.app" className="text-[#FF7F30] hover:text-[#FFA55A] font-semibold underline">support@lingu-pix.app</a></li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">2. What This Policy Covers</h2>
            <p className="text-[#666666] leading-relaxed mb-6">This Privacy Policy applies to:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>the LinguPix mobile application on iOS and Android;</li>
              <li>the LinguPix website at <code className="bg-gray-100 px-1 py-0.5 rounded text-sm text-[#FF7F30]">www.lingu-pix.app</code>;</li>
              <li>public account-management pages such as password reset and account deletion;</li>
              <li>customer support, feedback, and service-related communications.</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">3. Who Can Use LinguPix</h2>
            <p className="text-[#666666] leading-relaxed mb-6">LinguPix is designed to be created and managed by an adult account holder. Children use the learning experience under adult supervision. We do not allow children to independently create parent accounts in the app.</p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">4. Data We Collect</h2>
            <p className="text-[#666666] leading-relaxed mb-6">We collect only the data needed to operate the app, secure accounts, manage subscriptions, support learning progress, and respond to user requests.</p>
            <h3 className="text-2xl font-semibold text-[#333333] mb-4">4.1 Account and Authentication Data</h3>
            <p className="text-[#666666] leading-relaxed mb-6">When a parent or guardian creates or uses an account, we may collect:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>email address;</li>
              <li>account name or display name;</li>
              <li>Supabase authentication identifiers and session metadata;</li>
              <li>password reset and account verification workflow data.</li>
            </ul>
            <h3 className="text-2xl font-semibold text-[#333333] mb-4">4.2 Child Profile Data</h3>
            <p className="text-[#666666] leading-relaxed mb-6">When you create or manage a child profile, we may collect:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>child first name, nickname, or profile name entered by the adult user;</li>
              <li>age or age range;</li>
              <li>avatar choice, including emoji avatar or profile photo if you upload one;</li>
              <li>selected learning language;</li>
              <li>selected or preferred narrator voice;</li>
              <li>child-specific learning and reminder preferences.</li>
            </ul>
            <h3 className="text-2xl font-semibold text-[#333333] mb-4">4.3 Learning and Progress Data</h3>
            <p className="text-[#666666] leading-relaxed mb-6">To provide the core learning experience, we may collect and store:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>categories studied and words presented;</li>
              <li>swipe ratings and review outcomes;</li>
              <li>completed sessions and unfinished session state;</li>
              <li>streaks, stars, badges, rewards, and achievements;</li>
              <li>progress metrics such as words learned, completion, accuracy, and time spent;</li>
              <li>language-specific progress summaries.</li>
            </ul>
            <h3 className="text-2xl font-semibold text-[#333333] mb-4">4.4 Profile Photos and Media Access</h3>
            <p className="text-[#666666] leading-relaxed mb-6">If you choose to add or update a child profile photo, the app may request access to:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>the device camera;</li>
              <li>the device photo library or system photo picker.</li>
            </ul>
            <p className="text-[#666666] leading-relaxed mb-6">We only use this access to let you choose or capture a profile image. We do not request microphone, location, contacts, or call log access for LinguPix.</p>
            <h3 className="text-2xl font-semibold text-[#333333] mb-4">4.5 Notification Data</h3>
            <p className="text-[#666666] leading-relaxed mb-6">If you enable notifications, we may collect and store:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>Expo push token;</li>
              <li>app-generated device identifier used for notification routing and device licensing;</li>
              <li>platform and app version;</li>
              <li>notification preferences such as reminder time, timezone, reminder style, streak and achievement settings, and due-session reminders.</li>
            </ul>
            <h3 className="text-2xl font-semibold text-[#333333] mb-4">4.6 Subscription and Device Licensing Data</h3>
            <p className="text-[#666666] leading-relaxed mb-6">If you purchase or restore a subscription, we may process:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>subscription entitlement status;</li>
              <li>plan tier and product identifiers;</li>
              <li>subscription expiration and renewal status;</li>
              <li>app user identifier used with RevenueCat;</li>
              <li>licensed device records, including device identifier, device name, platform, and last-seen timestamps.</li>
            </ul>
            <p className="text-[#666666] leading-relaxed mb-6">Payment card data is not stored by LinguPix. Payments and subscription billing are handled by Apple App Store or Google Play.</p>
            <h3 className="text-2xl font-semibold text-[#333333] mb-4">4.7 Device and Security Data</h3>
            <p className="text-[#666666] leading-relaxed mb-6">To protect accounts and manage access, we may process:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>app-generated device identifiers;</li>
              <li>app version and platform metadata;</li>
              <li>security and audit events related to sign-in, sign-out, password reset, and account deletion.</li>
            </ul>
            <h3 className="text-2xl font-semibold text-[#333333] mb-4">4.8 Biometric Login</h3>
            <p className="text-[#666666] leading-relaxed mb-6">If you enable quick guardian login using Face ID, fingerprint, or other supported device biometrics:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>biometric matching is handled by your device operating system;</li>
              <li>LinguPix does not receive, store, or process raw fingerprint data, face scans, or biometric templates;</li>
              <li>we may securely store login session tokens on your device using secure local storage so you can sign in faster next time.</li>
            </ul>
            <h3 className="text-2xl font-semibold text-[#333333] mb-4">4.9 Support and Feedback Data</h3>
            <p className="text-[#666666] leading-relaxed mb-6">If you contact us or send feedback, we may process:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>your email address;</li>
              <li>your message content, subject, and related support details;</li>
              <li>limited metadata needed to respond and keep an audit trail of support requests.</li>
            </ul>
            <h3 className="text-2xl font-semibold text-[#333333] mb-4">4.10 Website and Public Account Deletion Data</h3>
            <p className="text-[#666666] leading-relaxed mb-6">When you use the website or public account management pages, we may process:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>IP address;</li>
              <li>browser and device metadata;</li>
              <li>pages visited and aggregated website usage data;</li>
              <li>the email address you enter on the public account deletion page;</li>
              <li>security metadata related to account deletion requests, including request IP and user agent.</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">5. How We Use Personal Data</h2>
            <p className="text-[#666666] leading-relaxed mb-6">We use personal data to:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>create and manage adult accounts;</li>
              <li>create and manage child profiles;</li>
              <li>deliver the learning experience and sync progress across devices;</li>
              <li>power swipe-based repetition, rewards, streaks, and progress tracking;</li>
              <li>enable notifications and reminder scheduling if you choose to turn them on;</li>
              <li>manage subscriptions, entitlements, and device licensing;</li>
              <li>deliver transactional emails such as password reset, account deletion, and other account-related service messages;</li>
              <li>respond to support, legal, privacy, and deletion requests;</li>
              <li>secure the app and website, detect abuse, prevent fraud, and maintain audit trails;</li>
              <li>improve performance, reliability, and product quality through limited analytics and diagnostics;</li>
              <li>comply with legal, tax, accounting, and regulatory obligations.</li>
            </ul>
            <p className="text-[#666666] leading-relaxed mb-6">We do not sell personal data. We do not use third-party advertising SDKs in the app, and we do not use personal data for third-party ad targeting.</p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">6. Legal Bases for Processing (GDPR)</h2>
            <p className="text-[#666666] leading-relaxed mb-6">If GDPR applies, we rely on the following legal bases depending on the processing activity:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li><strong>Performance of a contract</strong>: to provide account access, the core learning service, subscription features, device licensing, and account-related service functionality.</li>
              <li><strong>Legitimate interests</strong>: to secure the service, prevent abuse, maintain audit logs, debug issues, improve reliability, and understand aggregate usage trends.</li>
              <li><strong>Consent</strong>: when you grant access to the camera, photo library, notifications, biometrics-based quick login, or website analytics where consent is required.</li>
              <li><strong>Legal obligation</strong>: where we must retain or disclose information for tax, accounting, legal, or regulatory reasons.</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">7. When We Share Data</h2>
            <p className="text-[#666666] leading-relaxed mb-6">We share personal data only when necessary to operate the service, comply with the law, or provide a user-requested feature.</p>
            <h3 className="text-2xl font-semibold text-[#333333] mb-4">7.1 Service Providers and Platforms</h3>
            <p className="text-[#666666] leading-relaxed mb-6">We currently rely on the following providers:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li><strong>Supabase</strong>: backend infrastructure, authentication, database, storage, and server-side functions.</li>
              <li><strong>Loops</strong>: transactional email delivery, including account and support-related messages.</li>
              <li><strong>RevenueCat</strong>: subscription entitlement management and subscription event processing.</li>
              <li><strong>Apple App Store</strong>: iOS in-app purchases and subscription billing.</li>
              <li><strong>Google Play</strong>: Android in-app purchases and subscription billing.</li>
              <li><strong>Expo push infrastructure</strong>: push notification token issuance and push delivery routing.</li>
              <li><strong>Vercel Analytics (website only, if enabled)</strong>: aggregated, privacy-friendly website analytics.</li>
            </ul>
            <p className="text-[#666666] leading-relaxed mb-6">We may also disclose personal data:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>to comply with legal obligations or valid government requests;</li>
              <li>to protect the rights, safety, and security of LinguPix, our users, or others;</li>
              <li>as part of a merger, acquisition, or asset transfer, with appropriate notice where required.</li>
            </ul>
            <p className="text-[#666666] leading-relaxed mb-6">We do not publicly share child profile data with other app users.</p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">8. International Transfers</h2>
            <p className="text-[#666666] leading-relaxed mb-6">Some of our service providers may process data outside your country, including outside the European Economic Area. Where required, we rely on appropriate contractual, organizational, and technical safeguards for international transfers.</p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">9. Data Retention</h2>
            <p className="text-[#666666] leading-relaxed mb-6">We retain data only as long as reasonably necessary for the purposes described in this policy.</p>
            <p className="text-[#666666] leading-relaxed mb-6">In general:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li><strong>Account and child profile data</strong> are kept while the account remains active.</li>
              <li><strong>Learning progress and session data</strong> are kept while the account remains active, unless deleted earlier or no longer needed.</li>
              <li><strong>Notification preferences and device licensing records</strong> are kept while the related account or device relationship remains active, and may be retained for limited audit or fraud-prevention purposes.</li>
              <li><strong>Support and feedback messages</strong> are kept for as long as reasonably necessary to respond, follow up, and maintain service records.</li>
              <li><strong>Subscription and transactional records</strong> may be retained longer where required for accounting, contractual, fraud-prevention, or legal reasons.</li>
              <li><strong>Website analytics data</strong> is retained in aggregated or de-identified form where possible.</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">10. Account Deletion</h2>
            <p className="text-[#666666] leading-relaxed mb-6">LinguPix allows account deletion from inside the app and through a public website flow.</p>
            <h3 className="text-2xl font-semibold text-[#333333] mb-4">10.1 In-App Deletion</h3>
            <p className="text-[#666666] leading-relaxed mb-6">An adult account holder can request account deletion from the Settings area of the app.</p>
            <h3 className="text-2xl font-semibold text-[#333333] mb-4">10.2 Public Website Deletion</h3>
            <p className="text-[#666666] leading-relaxed mb-6">We also provide a public account deletion page at:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li><Link to="/delete-account" className="text-[#FF7F30] hover:text-[#FFA55A] font-semibold underline">www.lingu-pix.app/delete-account</Link></li>
            </ul>
            <p className="text-[#666666] leading-relaxed mb-6">The public flow works as follows:</p>
            <ol className="list-decimal pl-6 space-y-3 text-[#666666] mb-6">
              <li>You submit the email address associated with your account.</li>
              <li>We send a confirmation link to that email address.</li>
              <li>After you confirm the request, the account is blocked and enters a grace period.</li>
              <li>After the grace period ends, account data is permanently deleted unless cancellation is requested and accepted before final deletion.</li>
            </ol>
            <h3 className="text-2xl font-semibold text-[#333333] mb-4">10.3 What May Be Retained</h3>
            <p className="text-[#666666] leading-relaxed mb-6">Even after an account deletion request, we may retain limited data where required for:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>legal compliance;</li>
              <li>accounting or tax records;</li>
              <li>fraud prevention or security investigations;</li>
              <li>documenting deletion activity and resolving disputes.</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">11. Security</h2>
            <p className="text-[#666666] leading-relaxed mb-6">We use reasonable technical and organizational safeguards designed to protect personal data, including secure transport, access controls, secure storage, authentication protections, and restricted server-side access.</p>
            <p className="text-[#666666] leading-relaxed mb-6">No internet-connected service can guarantee absolute security, but we work to reduce risk and respond quickly to security issues.</p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">12. Your Privacy Rights</h2>
            <p className="text-[#666666] leading-relaxed mb-6">Depending on your location, you may have the right to:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>request access to your personal data;</li>
              <li>request correction of inaccurate data;</li>
              <li>request deletion of your personal data;</li>
              <li>object to or restrict certain processing;</li>
              <li>withdraw consent where processing is based on consent;</li>
              <li>request a portable copy of data where applicable;</li>
              <li>lodge a complaint with a competent supervisory authority.</li>
            </ul>
            <p className="text-[#666666] leading-relaxed mb-6">To exercise privacy rights, contact:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li><a href="mailto:contact@lingu-pix.app" className="text-[#FF7F30] hover:text-[#FFA55A] font-semibold underline">contact@lingu-pix.app</a></li>
            </ul>
            <p className="text-[#666666] leading-relaxed mb-6">We may ask you to verify your identity before completing a request.</p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">13. Children's Privacy</h2>
            <p className="text-[#666666] leading-relaxed mb-6">LinguPix is designed for children ages 3 to 7, but the account is intended to be created and managed by a parent, guardian, educator, or other adult account holder.</p>
            <p className="text-[#666666] leading-relaxed mb-6">We do not knowingly allow children to create adult accounts directly. Child profile information is provided and managed by the adult account holder.</p>
            <p className="text-[#666666] leading-relaxed mb-6">If you believe we processed child-related personal data in a way that should be reviewed or removed, contact:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li><a href="mailto:contact@lingu-pix.app" className="text-[#FF7F30] hover:text-[#FFA55A] font-semibold underline">contact@lingu-pix.app</a></li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">14. Cookies and Website Analytics</h2>
            <p className="text-[#666666] leading-relaxed mb-6">The mobile app does not use browser cookies.</p>
            <p className="text-[#666666] leading-relaxed mb-6">If the LinguPix website uses Vercel Analytics, it is used only to understand aggregated website traffic and performance. We do not use third-party advertising cookies or ad trackers on the website.</p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">15. Links to Other Websites</h2>
            <p className="text-[#666666] leading-relaxed mb-6">Our website or app may link to third-party services such as Apple, Google Play, or other external pages. We are not responsible for the privacy practices of third-party services, and we encourage you to review their policies.</p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">16. Changes to This Privacy Policy</h2>
            <p className="text-[#666666] leading-relaxed mb-6">We may update this Privacy Policy from time to time. When we do, we will update the "Last updated" date at the top of this page and, where appropriate, provide additional notice.</p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">17. Contact</h2>
            <p className="text-[#666666] leading-relaxed mb-6">For privacy, legal, GDPR, and deletion matters:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li><a href="mailto:contact@lingu-pix.app" className="text-[#FF7F30] hover:text-[#FFA55A] font-semibold underline">contact@lingu-pix.app</a></li>
            </ul>
            <p className="text-[#666666] leading-relaxed mb-6">For product support:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li><a href="mailto:support@lingu-pix.app" className="text-[#FF7F30] hover:text-[#FFA55A] font-semibold underline">support@lingu-pix.app</a></li>
            </ul>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;